import React, { useState, useEffect, useCallback } from "react";
import { useApi } from "../../hooks/useApi";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
    Bell,
    Check,
    CheckCheck,
    FolderKanban,
    Receipt,
    AlertTriangle,
    Settings,
} from "lucide-react";
import { cn, formatDateTime } from "../../lib/utils";

export const NotificationsPage = () => {
    const { get, put } = useApi();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchNotifications = useCallback(async () => {
        try {
            const data = await get("/notifications");
            setNotifications(data);
        } catch (error) {
            toast.error("Erreur lors du chargement des notifications");
        } finally {
            setLoading(false);
        }
    }, [get]);

    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    const markAsRead = async (notificationId) => {
        try {
            await put(`/notifications/${notificationId}/read`);
            setNotifications((prev) =>
                prev.map((n) => (n.notification_id === notificationId ? { ...n, is_read: true } : n))
            );
        } catch (error) {
            toast.error("Erreur lors de la mise à jour");
        }
    };

    const markAllAsRead = async () => {
        try {
            await put("/notifications/read-all");
            setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
            toast.success("Toutes les notifications marquées comme lues");
        } catch (error) {
            toast.error("Erreur lors de la mise à jour");
        }
    };

    const getIcon = (type) => {
        const icons = {
            project: FolderKanban,
            finance: Receipt,
            alert: AlertTriangle,
            admin: Settings,
        };
        return icons[type] || Bell;
    };

    const getPriorityColor = (priority) => {
        const colors = {
            low: "text-gray-500",
            normal: "text-blue-500",
            high: "text-orange-500",
            urgent: "text-red-500",
        };
        return colors[priority] || "text-gray-500";
    };

    const unreadCount = notifications.filter((n) => !n.is_read).length;

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl sm:text-3xl text-foreground">Notifications</h1>
                    <p className="text-muted-foreground mt-1">
                        {unreadCount > 0 ? `${unreadCount} non lue(s)` : "Toutes lues"}
                    </p>
                </div>

                {unreadCount > 0 && (
                    <Button variant="outline" onClick={markAllAsRead} data-testid="mark-all-read-btn">
                        <CheckCheck className="w-4 h-4 mr-2" />
                        Tout marquer comme lu
                    </Button>
                )}
            </div>

            {/* Notifications List */}
            <Card>
                <CardContent className="p-0">
                    {notifications.length === 0 ? (
                        <div className="text-center py-16 text-muted-foreground">
                            <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p>Aucune notification</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-border">
                            {notifications.map((notification) => {
                                const Icon = getIcon(notification.type);
                                return (
                                    <div
                                        key={notification.notification_id}
                                        className={cn(
                                            "flex items-start gap-4 p-4 transition-colors",
                                            !notification.is_read && "bg-primary/5"
                                        )}
                                        data-testid={`notification-${notification.notification_id}`}
                                    >
                                        <div
                                            className={cn(
                                                "w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0",
                                                notification.is_read ? "bg-secondary" : "bg-primary/10"
                                            )}
                                        >
                                            <Icon
                                                className={cn(
                                                    "w-5 h-5",
                                                    notification.is_read ? "text-muted-foreground" : "text-primary"
                                                )}
                                            />
                                        </div>

                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <div>
                                                    <h4
                                                        className={cn(
                                                            "font-medium",
                                                            !notification.is_read && "text-foreground"
                                                        )}
                                                    >
                                                        {notification.title}
                                                    </h4>
                                                    <p className="text-sm text-muted-foreground mt-0.5">
                                                        {notification.message}
                                                    </p>
                                                </div>
                                                <Badge
                                                    variant="outline"
                                                    className={cn("text-xs flex-shrink-0", getPriorityColor(notification.priority))}
                                                >
                                                    {notification.priority}
                                                </Badge>
                                            </div>

                                            <div className="flex items-center justify-between mt-2">
                                                <span className="text-xs text-muted-foreground">
                                                    {formatDateTime(notification.created_at)}
                                                </span>

                                                {!notification.is_read && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => markAsRead(notification.notification_id)}
                                                        data-testid={`mark-read-${notification.notification_id}`}
                                                    >
                                                        <Check className="w-4 h-4 mr-1" />
                                                        Marquer comme lu
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};
